import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import parseISO from 'date-fns/parseISO';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './customCalendar.css';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomText from 'components/common/CustomText/CustomText';
import CalendarIcon from 'icons/CalendarIcon';
import PersonAddIcon from 'icons/PersonAddIcon';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { useGetCases } from 'hooks/useCases';
import { useAuth } from 'context/AuthContext';
import { ECaseFilter } from 'enum/ECaseFilter';
import { ESearchBy } from 'enum/ESearchBy';
import { ESortType } from 'enum/ESortBy';
import { colors } from 'styles/colors';
import CalendarCaseItem from './CalendarCaseItem';

// ─── Persistence ──────────────────────────────────────────────────
const STATE_KEY = 'calendarViewState';

interface PersistedState {
  view: View;
  date: string; // ISO string
}

function readPersistedState(): PersistedState | null {
  try {
    const raw = sessionStorage.getItem(STATE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STATE_KEY); // consume once
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persistState(view: View, date: Date) {
  sessionStorage.setItem(STATE_KEY, JSON.stringify({
    view,
    date: date.toISOString(),
  }));
}

// ─── Calendar setup ───────────────────────────────────────────────
const locales = { 'en-US': enUS };

const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const DefaultEvent = ({ event }: { event: any }) => {
  const { procedure } = event;
  const label =
    procedure.procedure_name && procedure.procedure_name !== 'undefined'
      ? procedure.procedure_name
      : procedure.type;
  return <span style={{ fontSize: 12 }}>{label}</span>;
};

// Defined outside the component so its reference stays stable across renders.
// Navigation is embedded in event.navigateToPatient to avoid closure issues.
const DayEvent = ({ event }: { event: any }) => (
  <CalendarCaseItem item={event.procedure} onNavigate={event.navigateToPatient} />
);

// ─── Component ────────────────────────────────────────────────────
interface Props {
  fullWidth?: boolean;
  iconOnly?: boolean;
}

export default function CalendarView({ fullWidth = false, iconOnly = false }: Props) {
  const navigationType = useNavigationType();

  // Restore state only when the user navigated back (browser back = 'POP').
  // Any other navigation type (PUSH / REPLACE) means the user arrived fresh,
  // so we discard the saved state so the calendar doesn't auto-open.
  const restored = useMemo(() => {
    if (navigationType !== 'POP') {
      sessionStorage.removeItem(STATE_KEY);
      return null;
    }
    return readPersistedState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [open, setOpen] = useState(restored !== null);
  const [currentView, setCurrentView] = useState<View>(restored?.view ?? 'month');
  const [calendarDate, setCalendarDate] = useState<Date>(
    restored?.date ? new Date(restored.date) : new Date(),
  );

  const { user, doctor } = useAuth();
  const navigate = useNavigate();

  // Refs so navigateToPatient always reads the latest view/date even when
  // called from a stale closure inside the events memo.
  const currentViewRef = useRef(currentView);
  const calendarDateRef = useRef(calendarDate);
  useEffect(() => { currentViewRef.current = currentView; }, [currentView]);
  useEffect(() => { calendarDateRef.current = calendarDate; }, [calendarDate]);

  const { data, isLoading } = useGetCases(
    {
      userId: doctor?.id ?? user.id,
      page: 0,
      pageLimit: 9999,
      filter: ECaseFilter.ALL,
      search_by: ESearchBy.KEYWORD,
      sort_by: ESortType.DESC,
      value: '',
    },
    {
      // No `enabled` guard — fetch on mount so data is ready before the dialog opens.
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      // Treat the result as fresh for 5 minutes to avoid a re-fetch on every open.
      staleTime: 5 * 60 * 1000,
    },
  );

  // Save state before navigating away so the dialog re-opens at the same position.
  // Reads view/date through refs so it's always current even when called from
  // a stale closure (e.g. event objects built while data was last fetched).
  const navigateToPatient = useCallback((id: number) => {
    persistState(currentViewRef.current, calendarDateRef.current);
    setOpen(false);
    navigate(`/patients/${id}`);
  }, [navigate]);

  const events = useMemo(() => {
    const procedures: any[] = data?.data?.procedures ?? [];
    return procedures.map((proc) => ({
      title:
        proc.procedure_name && proc.procedure_name !== 'undefined'
          ? proc.procedure_name
          : proc.type,
      id: proc.id,
      start: parseISO(proc.date_of_procedure),
      end: parseISO(proc.date_of_procedure),
      allDay: true,
      procedure: proc,
      navigateToPatient: () => navigateToPatient(proc.id),
    }));
    // navigateToPatient is stable (useCallback with [navigate]) and reads
    // view/date through refs, so it is safe to omit from deps here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const hasEventsOnSelectedDay = useMemo(() => {
    if (currentView !== 'day') return true;
    const dayStr = format(calendarDate, 'yyyy-MM-dd');
    return events.some(e => format(e.start, 'yyyy-MM-dd') === dayStr);
  }, [events, currentView, calendarDate]);

  const eventStyleGetter = () => ({
    style: {
      backgroundColor: currentView === 'day' ? 'transparent' : colors.primary,
      borderRadius: currentView === 'day' ? 0 : '6px',
      color: colors.white,
      border: 'none',
      fontSize: 12,
      padding: currentView === 'day' ? 0 : '2px 6px',
    },
  });

  return (
    <>
      {iconOnly ? (
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            flex: 1,
            height: 40,
            borderRadius: '10px',
            border: `1px solid ${colors.primary}`,
            color: colors.primary,
            '&:hover': { borderColor: colors.primary },
          }}
        >
          <CalendarIcon size={14} />
        </IconButton>
      ) : (
        <CustomButton
          text="Calendar"
          variant="outlined"
          height={40}
          icon={<CalendarIcon size={14} />}
          onClick={() => setOpen(true)}
          fullWidth={fullWidth}
        />
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
        slotProps={{
          paper: {
            sx: {
              borderRadius: '15px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
                flexShrink: 0,
              }}
            >
              <CalendarIcon color={colors.primary} size={18} />
            </Box>
            <CustomText
              text="Calendar View"
              fontSize={16}
              color={colors.primary}
              fontWeight={600}
            />
          </Box>

          <IconButton
            onClick={() => setOpen(false)}
            disableRipple
            sx={{
              backgroundColor: colors.primary,
              height: 28,
              width: 28,
              '&:hover': { backgroundColor: colors.primary, opacity: 0.85 },
            }}
          >
            <CloseIcon sx={{ fontSize: 15, color: colors.white }} />
          </IconButton>
        </Box>

        <Divider />

        {/* Calendar body */}
        <DialogContent sx={{ p: 2.5, position: 'relative', minHeight: 540 }}>
          {isLoading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={36} sx={{ color: colors.primary }} />
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              <div className={`rbc-view-${currentView}`}>
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 520 }}
                  date={calendarDate}
                  view={currentView}
                  onNavigate={setCalendarDate}
                  onView={(view) => setCurrentView(view)}
                  selectable
                  onSelectSlot={(slot) => {
                    if (currentView !== 'day') {
                      setCalendarDate(slot.start);
                      setCurrentView('day');
                    }
                  }}
                  // Day view: navigation is handled inside CalendarCaseItem so the
                  // image tap can open the carousel without also triggering navigation.
                  // Month/Week view: clicking an event chip navigates directly.
                  onSelectEvent={(event: any) => {
                    if (currentView === 'day') return;
                    navigateToPatient(event.id);
                  }}
                  eventPropGetter={eventStyleGetter}
                  components={{
                    event: (props: any) =>
                      currentView === 'day' ? (
                        <DayEvent {...props} />
                      ) : (
                        <DefaultEvent {...props} />
                      ),
                  }}
                />
              </div>

              {currentView === 'day' && !hasEventsOnSelectedDay && (
                <Box
                  sx={{
                    position: 'absolute',
                    // sit below the day-header row (~65px) so the header stays visible
                    top: 65,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    pointerEvents: 'none',
                  }}
                >
                  <CalendarIcon size={36} color={colors.lightgray} />
                  <CustomText
                    text="No cases on this day"
                    fontSize={14}
                    color={colors.secondary}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        {/* Footer — only shown in day view */}
        {currentView === 'day' && (
          <>
            <Divider />
            <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
              <CustomButton
                text="Add Patient"
                height={40}
                icon={<PersonAddIcon size={14} />}
                onClick={() => {
                  setOpen(false);
                  navigate('/patients/add', { state: { date: calendarDate } });
                }}
              />
              <CustomButton
                text="Close"
                variant="outlined"
                intent="secondary"
                height={40}
                onClick={() => setOpen(false)}
              />
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
